import { WebsocketStatusType } from "../shared/types/pubsub";

/**
 * Returns true for any status that represents a connection that is down or has
 * permanently failed. Used to determine when to show error UI.
 */
export const isDisconnectedStatus = (status: WebsocketStatusType): boolean =>
  status === "disconnected" ||
  status === "stopped" ||
  status === "closed" ||
  status === "401" ||
  status === "failed";

/**
 * Derives the single worst-case connection status from the two websocket
 * connections (ShareDB and pubsub). ShareDB takes priority at each tier since
 * it's critical for document editing.
 *
 * Priority order (highest → lowest):
 *   "failed"        → retries exhausted, permanent until page reload
 *   disconnected    → hard failure (disconnected | stopped | closed | 401)
 *   "connecting"    → reconnect attempt in progress
 *   "connected"     → both healthy
 *
 * @param shareDBStatus null when ShareDB is not mounted (e.g. home page)
 */
export const getCombinedConnectionStatus = (
  shareDBStatus: WebsocketStatusType | null,
  pubsubStatus: WebsocketStatusType,
): WebsocketStatusType => {
  if (shareDBStatus === null) return pubsubStatus;

  const statuses = [shareDBStatus, pubsubStatus];

  if (statuses.includes("failed")) return "failed";

  const disconnectedStatus = statuses.find(isDisconnectedStatus);
  if (disconnectedStatus) return disconnectedStatus;

  if (statuses.includes("connecting")) return "connecting";

  return "connected";
};
